import {
  Bolt,
  Edit,
  ExpandLess,
  ExpandMore,
  MoneyOff,
  Receipt,
  Save,
  Cancel,
  DeleteOutline,
  Refresh,
  Search,
  WarningAmberRounded,
} from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TablePagination,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { API_URL, monthsForFilter } from "const/constants";
import {
  useInvalidateProviderAdmissions,
  useProviderAdmissionByClient,
  useProviderAdmissionPage,
  useProviderClaimSummary,
} from "utils/providerAdmissionService";
import axios from "axios";
import { useEffect, useState } from "react";
import { IoArrowBack } from "react-icons/io5";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ProviderInvoiceModal from "./ProviderInvoiceModal";
import DirectClaimDialog from "./DirectClaimDialog";

const YEARS = Array.from({ length: 10 }, (_, i) => String(new Date().getFullYear() + 3 - i));
const fmtMoney = (value) => `$${Number(value || 0).toFixed(2)}`;
const isPartialInvoice = (term) => term?.existingInvoicePaymentStatus === "PARTIALLY_PAID";
const isClaimedButNotSent = (term) => Boolean(term?.claimedButNotSent);
const termRowKey = (term) => term.id ?? `unsent-${term.sourceAdmissionTermId}`;

// A component only "applies" (is actually owed) when it has a non-zero amount AND hasn't been
// explicitly flagged not-applicable (a waived/never-owed component, distinct from "just $0").
const commissionApplies = (term) =>
  !term?.commissionNotApplicable && (Number(term?.commission) || 0) > 0;
const bonusApplies = (term) => !term?.bonusNotApplicable && (Number(term?.bonus) || 0) > 0;

const commissionSettled = (term) =>
  !commissionApplies(term) ||
  Boolean(term?.isCommissionInvoicePaid) ||
  Boolean(term?.isCommissionClaimed);
const bonusSettled = (term) =>
  !bonusApplies(term) || Boolean(term?.isBonusInvoicePaid) || Boolean(term?.isBonusClaimed);

const isTermPending = (term) => {
  if (!commissionApplies(term) && !bonusApplies(term)) return false;
  return !commissionSettled(term) || !bonusSettled(term);
};

const isTermFullySettled = (term) =>
  (commissionApplies(term) || bonusApplies(term)) && commissionSettled(term) && bonusSettled(term);

const isTermAwaitingInvoicePayment = (term) => Boolean(term?.hasInvoice) && isTermPending(term);

const AddProvider = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const accountProvider = state?.item; // { id, universityName, universityId, ... }
  const targetClientId = state?.targetClientId;

  const [expandedIds, setExpandedIds] = useState(new Set());
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, id: null });
  // { admission, term } when the direct-claim dialog is open
  const [directClaim, setDirectClaim] = useState(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // ALL | REMAINING | CLAIMED — the server owns the definition of "remaining" and always sorts
  // those rows first, so the one unclaimed client on a 19-row provider is never stranded on page 2.
  const [claimFilter, setClaimFilter] = useState("ALL");

  const providerId = accountProvider?.accountProviderId;
  const invalidateProvider = useInvalidateProviderAdmissions(providerId);

  const { data: listPage, isFetching: loading } = useProviderAdmissionPage(providerId, {
    page,
    size: rowsPerPage,
    search,
    claimFilter,
  });
  const listAdmissions = listPage?.content ?? [];
  const listTotal = listPage?.totalItems ?? 0;

  const { data: claimSummary } = useProviderClaimSummary(providerId);
  const totalUnclaimed = claimSummary?.pendingTerms ?? 0;

  // Deep links name a client, not a page. Resolve just that client, then hand their name to the
  // search box so the list they land on is one they can clear back out of.
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);
  const { data: deepLinkAdmission } = useProviderAdmissionByClient(
    providerId,
    deepLinkHandled ? null : targetClientId
  );

  useEffect(() => {
    if (deepLinkHandled || !deepLinkAdmission) return;
    setExpandedIds(new Set([deepLinkAdmission.id]));
    setSearchInput(deepLinkAdmission.clientName || "");
    setSearch(deepLinkAdmission.clientName || "");
    setPage(0);
    setDeepLinkHandled(true);
  }, [deepLinkAdmission, deepLinkHandled]);

  useEffect(() => {
    const uniId = Number(accountProvider?.universityId);
    if (providerId && Number.isFinite(uniId) && uniId > 0) loadAgents(uniId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId]);

  // Debounce free-text typing before it becomes the committed `search` that triggers a fetch.
  useEffect(() => {
    const handle = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setPage(0);
      }
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const refreshAll = () => invalidateProvider();

  const handleClaimFilterChange = (_event, next) => {
    // ToggleButtonGroup hands back null when the active button is clicked again; keep the
    // current selection instead of silently dropping to an unfiltered list.
    if (!next || next === claimFilter) return;
    setClaimFilter(next);
    setPage(0);
  };

  const loadAgents = async (uniId) => {
    try {
      const res = await axios.get(`${API_URL}/universities/${uniId}/agents`);
      setAgents(res?.data?.data || []);
    } catch {
      setAgents([]);
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleClaimTerm = async (termId, admissionId) => {
    try {
      await axios.post(`${API_URL}/provider-admissions/terms/${termId}/claim`);
      toast.success("Term claimed");
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to claim term");
    }
  };

  const handleUnclaimTerm = async (termId) => {
    try {
      await axios.post(`${API_URL}/provider-admissions/terms/${termId}/unclaim`);
      toast.success("Term unclaimed");
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to unclaim term");
    }
  };

  const handleRemoveTerm = (termId) => {
    setConfirmDialog({ open: true, type: "term", id: termId });
  };

  const handleDeleteAdmission = (admissionId) => {
    setConfirmDialog({ open: true, type: "admission", id: admissionId });
  };

  const handleConfirm = async () => {
    const { type, id } = confirmDialog;
    setConfirmDialog({ open: false, type: null, id: null });
    try {
      if (type === "term") {
        await axios.delete(`${API_URL}/provider-admissions/terms/${id}`);
        toast.success("Term removed from provider");
      } else if (type === "admission") {
        await axios.delete(`${API_URL}/provider-admissions/${id}`);
        toast.success("Admission entry removed");
      }
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Action failed");
    }
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-app-surface text-app-text min-h-[70vh]">
          {/* Header */}
          <div className="rounded-t mb-0 px-4 py-3 border-b">
            <div className="flex items-center justify-between">
              <IoArrowBack className="text-xl cursor-pointer" onClick={() => navigate(-1)} />
              <div className="flex-1 flex items-center justify-center gap-1">
                <h1 className="text-2xl uppercase font-bold text-center">
                  {accountProvider?.universityName || "Provider"} — Admissions
                </h1>
                <Tooltip title="Refresh" arrow>
                  <IconButton size="small" onClick={refreshAll} disabled={loading}>
                    <Refresh fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
              <div className="flex gap-2 items-center">
                {totalUnclaimed > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Receipt />}
                    onClick={() => setInvoiceModalOpen(true)}
                  >
                    Generate Invoice
                    <Chip
                      label={totalUnclaimed}
                      size="small"
                      color="warning"
                      sx={{ ml: 1, height: 18, fontSize: 10 }}
                    />
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="p-4">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <TextField
                size="small"
                placeholder="Search by client, course, or agent…"
                value={searchInput}
                onChange={handleSearchChange}
                sx={{ width: 320, maxWidth: "100%" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <div className="flex items-center gap-3 flex-wrap">
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={claimFilter}
                  onChange={handleClaimFilterChange}
                  aria-label="Claim status filter"
                >
                  <ToggleButton value="ALL" sx={{ textTransform: "none", px: 1.5 }}>
                    All
                  </ToggleButton>
                  <ToggleButton value="REMAINING" sx={{ textTransform: "none", px: 1.5 }}>
                    Remaining
                  </ToggleButton>
                  <ToggleButton value="CLAIMED" sx={{ textTransform: "none", px: 1.5 }}>
                    Claimed
                  </ToggleButton>
                </ToggleButtonGroup>
                {listTotal > 0 && (
                  <span className="text-xs text-app-text-secondary">
                    {listTotal} client{listTotal !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center mt-10">
                <CircularProgress />
              </div>
            ) : listAdmissions.length === 0 ? (
              <p className="text-app-text-muted italic text-center mt-10">
                {search.trim()
                  ? "No admissions match your search."
                  : claimFilter === "REMAINING"
                  ? "Nothing left to claim for this provider."
                  : claimFilter === "CLAIMED"
                  ? "No claimed admissions for this provider yet."
                  : "No admissions or claimed terms for this provider yet."}
              </p>
            ) : (
              <>
                <div className="flex flex-col gap-3">
                  {listAdmissions.map((admission) => (
                    <ProviderAdmissionCard
                      key={admission.id}
                      admission={admission}
                      expanded={expandedIds.has(admission.id)}
                      onToggle={() => toggleExpand(admission.id)}
                      agents={agents}
                      onClaimTerm={handleClaimTerm}
                      onUnclaimTerm={handleUnclaimTerm}
                      onRemoveTerm={handleRemoveTerm}
                      onDeleteAdmission={handleDeleteAdmission}
                      onDirectClaim={(adm, term) => setDirectClaim({ admission: adm, term })}
                      onRefresh={refreshAll}
                      accountProviderId={accountProvider.accountProviderId}
                    />
                  ))}
                </div>

                <TablePagination
                  component="div"
                  count={listTotal}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50]}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <ProviderInvoiceModal
        open={invoiceModalOpen}
        onClose={() => setInvoiceModalOpen(false)}
        accountProviderId={providerId}
        onSuccess={refreshAll}
      />

      <DirectClaimDialog
        open={Boolean(directClaim)}
        onClose={() => setDirectClaim(null)}
        admission={directClaim?.admission}
        term={directClaim?.term}
        accountProviderId={accountProvider?.accountProviderId}
        onClaimed={refreshAll}
      />

      {/* Confirm Dialog — replaces window.confirm */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, type: null, id: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className="flex items-center gap-2">
          <WarningAmberRounded className="text-amber-500" />
          {confirmDialog.type === "term" ? "Remove Term from Provider?" : "Delete Admission Entry?"}
        </DialogTitle>
        <DialogContent>
          {confirmDialog.type === "term" ? (
            <p className="text-sm text-app-text">
              This term will be removed from the provider and marked as unsent. It can be re-sent to
              the provider again later.
            </p>
          ) : (
            <p className="text-sm text-app-text">
              This admission entry has no terms and will be permanently deleted. This cannot be
              undone.
            </p>
          )}
        </DialogContent>
        <DialogActions className="py-2 px-3 gap-2">
          <Button
            onClick={() => setConfirmDialog({ open: false, type: null, id: null })}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} variant="contained" color="error">
            {confirmDialog.type === "term" ? "Remove" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// Individual Admission Card
function ProviderAdmissionCard({
  admission,
  expanded,
  onToggle,
  agents,
  onClaimTerm,
  onUnclaimTerm,
  onRemoveTerm,
  onDeleteAdmission,
  onDirectClaim,
  onRefresh,
  accountProviderId,
}) {
  const navigate = useNavigate();
  const [editingTermId, setEditingTermId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [agentEdit, setAgentEdit] = useState(false);
  const [agentValue, setAgentValue] = useState(admission.agentName ?? "");

  // Remaining-to-claim = terms with at least one non-zero commission/bonus component still
  // unsettled. Terms where both components are zero/null have nothing to claim and never count.
  // Claimed-but-not-sent terms are their own read-only bucket and are excluded from all counts below.
  const pendingCount = admission.terms.filter(
    (t) => !isClaimedButNotSent(t) && isTermPending(t)
  ).length;
  const claimedCount = admission.terms.filter((t) => {
    if (isClaimedButNotSent(t)) return false;
    const hasAnyAmount = (Number(t.commission) || 0) > 0 || (Number(t.bonus) || 0) > 0;
    return hasAnyAmount && !isTermPending(t);
  }).length;
  const claimedNotSentCount = admission.terms.filter(isClaimedButNotSent).length;
  const invoicedCount = admission.terms.filter(
    (t) => !isClaimedButNotSent(t) && isTermAwaitingInvoicePayment(t)
  ).length;
  const partialInvoiceCount = admission.terms.filter(
    (t) => !isClaimedButNotSent(t) && isPartialInvoice(t)
  ).length;

  const saveAgentName = async () => {
    try {
      if (!admission.terms.length || admission.claimedOnlyView) {
        setAgentEdit(false);
        return;
      }
      await axios.put(`${API_URL}/provider-admissions/terms/${admission.terms[0].id}`, {
        agentName: agentValue,
      });
      toast.success("Agent name updated");
      setAgentEdit(false);
      onRefresh();
    } catch {
      toast.error("Failed to update agent");
    }
  };

  const startEditTerm = (term) => {
    if (isClaimedButNotSent(term) || term.id == null) return;
    setEditingTermId(term.id);
    setEditValues({
      ...term,
      commissionGst: term.commissionGst ?? "yes",
      bonusGst: term.bonusGst ?? "yes",
      admissionDate:
        term.admissionDate ??
        (term.year && term.month ? `${term.year}-${String(term.month).padStart(2, "0")}-01` : ""),
    });
  };

  const cancelEditTerm = () => {
    setEditingTermId(null);
    setEditValues({});
  };

  const saveEditTerm = async () => {
    try {
      await axios.put(`${API_URL}/provider-admissions/terms/${editingTermId}`, {
        perSemFee: editValues.perSemFee,
        commission: editValues.commission,
        bonus: editValues.bonus,
        gst: editValues.commissionGst,
        commissionGst: editValues.commissionGst,
        bonusGst: editValues.bonusGst,
        commissionNotApplicable: editValues.commissionNotApplicable ?? null,
        bonusNotApplicable: editValues.bonusNotApplicable ?? null,
        censusDate: editValues.censusDate,
        year: editValues.year,
        month: editValues.month,
        admissionDate:
          editValues.admissionDate ??
          (editValues.year && editValues.month
            ? `${editValues.year}-${editValues.month}-01`
            : null),
      });
      toast.success("Term updated");
      setEditingTermId(null);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update term");
    }
  };

  const setEV = (field, val) => setEditValues((p) => ({ ...p, [field]: val }));

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Card header */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-app-surface-muted cursor-pointer hover:bg-app-surface-hover"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <div>
            <span
              className="font-semibold text-app-text hover:text-primary hover:underline cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                if (admission.clientId) navigate(`/admin/client/view/${admission.clientId}`);
              }}
            >
              {admission.clientName || "Unknown Client"}
            </span>
            <span className="text-xs text-app-text-secondary ml-2">
              {admission.courseName && `• ${admission.courseName}`}
            </span>
          </div>
          <div className="flex gap-1">
            <Chip
              label={`${admission.terms.length} term${admission.terms.length !== 1 ? "s" : ""}`}
              size="small"
              sx={{ fontSize: 10, height: 18 }}
            />
            {pendingCount > 0 && (
              <Tooltip title="Terms with commission and/or bonus still unclaimed">
                <Chip
                  label={`${pendingCount} pending`}
                  size="small"
                  color="warning"
                  sx={{ fontSize: 10, height: 18 }}
                />
              </Tooltip>
            )}
            {claimedNotSentCount > 0 && (
              <Chip
                label={`${claimedNotSentCount} claimed (not sent)`}
                size="small"
                variant="outlined"
                sx={{ fontSize: 10, height: 18 }}
              />
            )}
            {claimedCount > 0 && (
              <Chip
                label={`${claimedCount} claimed`}
                size="small"
                color="success"
                sx={{ fontSize: 10, height: 18 }}
              />
            )}
            {invoicedCount > 0 && (
              <Tooltip title="Terms invoiced but not yet fully paid or claimed">
                <Chip
                  label={`${invoicedCount} invoiced`}
                  size="small"
                  color="info"
                  sx={{ fontSize: 10, height: 18 }}
                />
              </Tooltip>
            )}
            {partialInvoiceCount > 0 && (
              <Chip
                label={`${partialInvoiceCount} partial`}
                size="small"
                color="warning"
                sx={{ fontSize: 10, height: 18 }}
              />
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Agent name display/edit */}
          {agentEdit ? (
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <Autocomplete
                freeSolo
                size="small"
                options={agents}
                getOptionLabel={(o) => (typeof o === "string" ? o : o?.name ?? "")}
                value={agentValue}
                onChange={(_, v) => setAgentValue(typeof v === "string" ? v : v?.name ?? "")}
                onInputChange={(_, v) => setAgentValue(v)}
                renderInput={(p) => (
                  <TextField {...p} size="small" placeholder="Agent name" sx={{ width: 160 }} />
                )}
              />
              <IconButton size="small" onClick={saveAgentName}>
                <Save sx={{ fontSize: 14, color: "green" }} />
              </IconButton>
              <IconButton size="small" onClick={() => setAgentEdit(false)}>
                <Cancel sx={{ fontSize: 14, color: "red" }} />
              </IconButton>
            </div>
          ) : (
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <span className="text-xs text-app-text-secondary">
                {admission.agentName || "No agent"}
              </span>
              <Tooltip title="Edit agent name">
                <IconButton
                  size="small"
                  disabled={admission.claimedOnlyView}
                  onClick={() => {
                    setAgentEdit(true);
                    setAgentValue(admission.agentName ?? "");
                  }}
                >
                  <Edit sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            </div>
          )}
          {admission.terms.length === 0 && !admission.claimedOnlyView && (
            <Tooltip title="Delete this empty admission entry">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteAdmission(admission.id);
                }}
              >
                <DeleteOutline sx={{ fontSize: 16, color: "#dc2626" }} />
              </IconButton>
            </Tooltip>
          )}
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </div>
      </div>
      {/* Terms table */}
      <Collapse in={expanded}>
        <div className="overflow-x-auto p-3">
          {admission?.terms?.length === 0 ? (
            <p className="text-xs text-app-text-muted italic">No terms.</p>
          ) : (
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-app-surface-muted">
                  <th className="px-2 py-1 border text-left">Term</th>
                  <th className="px-2 py-1 border text-left">Date</th>
                  <th className="px-2 py-1 border text-left">Census Date</th>
                  <th className="px-2 py-1 border text-left">Per Sem Fee</th>
                  <th className="px-2 py-1 border text-left">Commission</th>
                  <th className="px-2 py-1 border text-left">Comm. GST</th>
                  <th className="px-2 py-1 border text-left">Bonus</th>
                  <th className="px-2 py-1 border text-left">Bonus GST</th>
                  <th className="px-2 py-1 border text-left">Invoice</th>
                  <th className="px-2 py-1 border text-left">Claimed</th>
                  <th className="px-2 py-1 border text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {admission?.terms?.map((term) => {
                  const readOnlyNotSent = isClaimedButNotSent(term);
                  const isEd = !readOnlyNotSent && editingTermId === term.id;
                  const ev = editValues;
                  return (
                    <tr
                      key={termRowKey(term)}
                      className={
                        readOnlyNotSent
                          ? "bg-app-surface-muted/70"
                          : term.isCommissionInvoicePaid || term.isBonusInvoicePaid
                          ? "bg-green-50"
                          : isPartialInvoice(term)
                          ? "bg-amber-50"
                          : ""
                      }
                    >
                      <td className="px-2 py-1 border font-medium">
                        <div className="flex items-center gap-1">
                          <span>T{term.termNumber}</span>
                          {readOnlyNotSent && (
                            <Chip
                              label="Not sent"
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 9, height: 16 }}
                            />
                          )}
                        </div>
                      </td>

                      <td className="px-2 py-1 border">
                        {isEd ? (
                          <input
                            type="date"
                            className="border rounded px-1 text-xs w-32"
                            value={
                              ev.admissionDate ??
                              (ev.year && ev.month
                                ? `${ev.year}-${String(ev.month).padStart(2, "0")}-01`
                                : "")
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              const parts = val ? val.split("-") : [];
                              setEV("admissionDate", val);
                              setEV("year", parts[0] ?? "");
                              setEV("month", parts[1] ?? "");
                            }}
                          />
                        ) : term.admissionDate ? (
                          <span>{term.admissionDate}</span>
                        ) : term.year && term.month ? (
                          <span>
                            {term.year}-{String(term.month).padStart(2, "0")}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>

                      <td className="px-2 py-1 border">
                        {isEd ? (
                          <input
                            type="date"
                            className="border rounded px-1 text-xs w-32"
                            value={ev.censusDate ?? ""}
                            onChange={(e) => setEV("censusDate", e.target.value)}
                          />
                        ) : term.censusDate ? (
                          <Tooltip title="Census Date">
                            <span className="font-medium text-orange-600">{term.censusDate}</span>
                          </Tooltip>
                        ) : (
                          "-"
                        )}
                      </td>

                      <td className="px-2 py-1 border">
                        {isEd ? (
                          <input
                            type="number"
                            className="border rounded px-1 text-xs w-20"
                            value={ev.perSemFee ?? ""}
                            onChange={(e) => setEV("perSemFee", e.target.value)}
                          />
                        ) : term.perSemFee ? (
                          `$${term.perSemFee}`
                        ) : (
                          "-"
                        )}
                      </td>

                      <td className="px-2 py-1 border">
                        {isEd ? (
                          <Tooltip
                            title={
                              ev.isCommissionInvoiceLocked
                                ? "Commission invoice paid — cannot edit"
                                : ""
                            }
                            arrow
                          >
                            <input
                              type="number"
                              className={`border rounded px-1 text-xs w-20 ${
                                ev.isCommissionInvoiceLocked
                                  ? "bg-app-surface-muted text-app-text-muted cursor-not-allowed"
                                  : ""
                              }`}
                              value={ev.commission ?? ""}
                              onChange={(e) => setEV("commission", e.target.value)}
                              disabled={Boolean(ev.isCommissionInvoiceLocked)}
                            />
                          </Tooltip>
                        ) : term.commission ? (
                          `$${term.commission}`
                        ) : term.commissionNotApplicable ? (
                          <span className="text-app-text-muted italic">N/A</span>
                        ) : (
                          "-"
                        )}
                        {isEd && (
                          <Tooltip
                            title={
                              (Number(ev.commission) || 0) > 0
                                ? "Clear the commission amount first to mark it not applicable"
                                : "No commission owed for this term"
                            }
                          >
                            <label className="flex items-center gap-1 text-[10px] text-app-text-secondary mt-0.5 cursor-pointer">
                              <input
                                type="checkbox"
                                className="h-3 w-3"
                                checked={Boolean(ev.commissionNotApplicable)}
                                disabled={(Number(ev.commission) || 0) > 0}
                                onChange={(e) => setEV("commissionNotApplicable", e.target.checked)}
                              />
                              N/A
                            </label>
                          </Tooltip>
                        )}
                      </td>

                      <td className="px-2 py-1 border">
                        {isEd ? (
                          <Tooltip
                            title={
                              ev.isCommissionInvoiceLocked
                                ? "Commission invoice paid — cannot edit"
                                : ""
                            }
                            arrow
                          >
                            <select
                              className={`border rounded px-1 text-xs w-16 ${
                                ev.isCommissionInvoiceLocked
                                  ? "bg-app-surface-muted text-app-text-muted cursor-not-allowed"
                                  : ""
                              }`}
                              value={ev.commissionGst ?? ""}
                              onChange={(e) => setEV("commissionGst", e.target.value)}
                              disabled={Boolean(ev.isCommissionInvoiceLocked)}
                            >
                              <option value="">-</option>
                              <option value="yes">Yes</option>
                              <option value="no">No</option>
                            </select>
                          </Tooltip>
                        ) : (
                          term.commissionGst || "-"
                        )}
                      </td>

                      <td className="px-2 py-1 border">
                        {isEd ? (
                          <Tooltip
                            title={
                              ev.isBonusInvoiceLocked ? "Bonus invoice paid — cannot edit" : ""
                            }
                            arrow
                          >
                            <input
                              type="number"
                              className={`border rounded px-1 text-xs w-20 ${
                                ev.isBonusInvoiceLocked
                                  ? "bg-app-surface-muted text-app-text-muted cursor-not-allowed"
                                  : ""
                              }`}
                              value={ev.bonus ?? ""}
                              onChange={(e) => setEV("bonus", e.target.value)}
                              disabled={Boolean(ev.isBonusInvoiceLocked)}
                            />
                          </Tooltip>
                        ) : term.bonus ? (
                          `$${term.bonus}`
                        ) : term.bonusNotApplicable ? (
                          <span className="text-app-text-muted italic">N/A</span>
                        ) : (
                          "-"
                        )}
                        {isEd && (
                          <Tooltip
                            title={
                              (Number(ev.bonus) || 0) > 0
                                ? "Clear the bonus amount first to mark it not applicable"
                                : "No bonus owed for this term"
                            }
                          >
                            <label className="flex items-center gap-1 text-[10px] text-app-text-secondary mt-0.5 cursor-pointer">
                              <input
                                type="checkbox"
                                className="h-3 w-3"
                                checked={Boolean(ev.bonusNotApplicable)}
                                disabled={(Number(ev.bonus) || 0) > 0}
                                onChange={(e) => setEV("bonusNotApplicable", e.target.checked)}
                              />
                              N/A
                            </label>
                          </Tooltip>
                        )}
                      </td>

                      <td className="px-2 py-1 border">
                        {isEd ? (
                          <Tooltip
                            title={
                              ev.isBonusInvoiceLocked ? "Bonus invoice paid — cannot edit" : ""
                            }
                            arrow
                          >
                            <select
                              className={`border rounded px-1 text-xs w-16 ${
                                ev.isBonusInvoiceLocked
                                  ? "bg-app-surface-muted text-app-text-muted cursor-not-allowed"
                                  : ""
                              }`}
                              value={ev.bonusGst ?? ""}
                              onChange={(e) => setEV("bonusGst", e.target.value)}
                              disabled={Boolean(ev.isBonusInvoiceLocked)}
                            >
                              <option value="">-</option>
                              <option value="yes">Yes</option>
                              <option value="no">No</option>
                            </select>
                          </Tooltip>
                        ) : (
                          term.bonusGst || "-"
                        )}
                      </td>

                      <td className="px-2 py-1 border">
                        {readOnlyNotSent ? (
                          <span className="text-app-text-muted">—</span>
                        ) : term.hasInvoice ? (
                          <div className="flex flex-col gap-0.5">
                            {term.commissionInvoiceNumber && (
                              <Tooltip
                                title={`Commission — View Invoice ${term.commissionInvoiceNumber}`}
                                arrow
                              >
                                <Chip
                                  label={`Comm #${term.commissionInvoiceNumber}`}
                                  size="small"
                                  color="info"
                                  onClick={() =>
                                    navigate(`/admin/invoice/view/${term.commissionInvoiceNumber}`)
                                  }
                                  sx={{ fontSize: 10, height: 18, cursor: "pointer" }}
                                />
                              </Tooltip>
                            )}
                            {term.bonusInvoiceNumber &&
                              term.bonusInvoiceNumber !== term.commissionInvoiceNumber && (
                                <Tooltip
                                  title={`Bonus — View Invoice KES-INV-${term.bonusInvoiceNumber}`}
                                  arrow
                                >
                                  <Chip
                                    label={`Bonus #${term.bonusInvoiceNumber}`}
                                    size="small"
                                    color="secondary"
                                    onClick={() =>
                                      navigate(`/admin/invoice/view/${term.bonusInvoiceNumber}`)
                                    }
                                    sx={{ fontSize: 10, height: 18, cursor: "pointer" }}
                                  />
                                </Tooltip>
                              )}
                            {!term.commissionInvoiceNumber && !term.bonusInvoiceNumber && (
                              <Tooltip
                                title={
                                  term.existingInvoiceNumber
                                    ? `View Invoice KES-INV-${term.existingInvoiceNumber}`
                                    : "View Invoice"
                                }
                                arrow
                              >
                                <Chip
                                  label={
                                    term.existingInvoiceNumber
                                      ? `Invoiced #${term.existingInvoiceNumber}`
                                      : "Invoiced"
                                  }
                                  size="small"
                                  color="info"
                                  onClick={() =>
                                    navigate(`/admin/invoice/view/${term.existingInvoiceNumber}`)
                                  }
                                  sx={{ fontSize: 10, height: 18, cursor: "pointer" }}
                                />
                              </Tooltip>
                            )}
                          </div>
                        ) : (
                          <span className="text-app-text-muted">—</span>
                        )}
                      </td>

                      <td className="px-2 py-1 border">
                        {readOnlyNotSent ? (
                          <Tooltip title="Claimed manually in account details — not sent to provider">
                            <div className="flex flex-col gap-0.5">
                              {term.isCommissionClaimed && (
                                <span className="text-green-700">✓ Commission</span>
                              )}
                              {term.isBonusClaimed && (
                                <span className="text-green-700">✓ Bonus</span>
                              )}
                              <span className="text-app-text-muted text-[10px]">Account claim</span>
                            </div>
                          </Tooltip>
                        ) : (
                          (() => {
                            const cApplies = commissionApplies(term);
                            const bApplies = bonusApplies(term);

                            // null = this component doesn't apply to the term at all (N/A or $0) —
                            // omitted from the row entirely, never nagging for an invoice.
                            const commissionLabel = !cApplies
                              ? null
                              : term.isCommissionInvoicePaid
                              ? "✓ Commission"
                              : term.isCommissionClaimed
                              ? "✓ Commission (claimed)"
                              : term.hasCommissionInvoice
                              ? "Invoice raised"
                              : "Commission uninvoiced";
                            const bonusLabel = !bApplies
                              ? null
                              : term.isBonusInvoicePaid
                              ? "✓ Bonus"
                              : term.isBonusClaimed
                              ? "✓ Bonus (claimed)"
                              : term.hasBonusInvoice
                              ? "Invoice raised"
                              : "Bonus uninvoiced";

                            if (commissionLabel === null && bonusLabel === null) {
                              return isPartialInvoice(term) ? (
                                <Tooltip title="Partial claim is recorded against the overall provider invoice amount, not a specific commission or bonus row.">
                                  <div className="flex flex-col gap-0.5">
                                    <span className="text-amber-700 font-medium">
                                      Partially claimed
                                    </span>
                                    <span className="text-app-text-secondary">
                                      {fmtMoney(term.existingInvoicePartialPaidAmount)} /{" "}
                                      {fmtMoney(term.existingInvoiceTotalAmount)}
                                    </span>
                                  </div>
                                </Tooltip>
                              ) : (
                                <span className="text-app-text-muted">Nothing owed</span>
                              );
                            }

                            const settledClass = (settled, hasInvoice) =>
                              settled
                                ? "text-green-700"
                                : hasInvoice
                                ? "text-amber-600"
                                : "text-app-text-muted";

                            return (
                              <Tooltip
                                title={[
                                  !cApplies
                                    ? null
                                    : term.isCommissionInvoicePaid
                                    ? "Commission: Paid ✓"
                                    : term.isCommissionClaimed
                                    ? "Commission: Claimed manually (no invoice)"
                                    : term.hasCommissionInvoice
                                    ? "Commission: Invoice raised (unpaid)"
                                    : "Commission: Not yet invoiced",
                                  !bApplies
                                    ? null
                                    : term.isBonusInvoicePaid
                                    ? "Bonus: Paid ✓"
                                    : term.isBonusClaimed
                                    ? "Bonus: Claimed manually (no invoice)"
                                    : term.hasBonusInvoice
                                    ? "Bonus: Invoice raised (unpaid)"
                                    : "Bonus: Not yet invoiced",
                                ]
                                  .filter(Boolean)
                                  .join(" | ")}
                              >
                                <div className="flex flex-col gap-0.5">
                                  {commissionLabel !== null && (
                                    <span
                                      className={settledClass(
                                        commissionSettled(term),
                                        term.hasCommissionInvoice
                                      )}
                                    >
                                      {commissionLabel}
                                    </span>
                                  )}
                                  {bonusLabel !== null && (
                                    <span
                                      className={settledClass(
                                        bonusSettled(term),
                                        term.hasBonusInvoice
                                      )}
                                    >
                                      {bonusLabel}
                                    </span>
                                  )}
                                </div>
                              </Tooltip>
                            );
                          })()
                        )}
                      </td>

                      <td className="px-2 py-1 border">
                        <div className="flex gap-1 items-center">
                          {readOnlyNotSent ? (
                            <Tooltip title="Read-only — edit from client account details">
                              <span className="text-xs text-app-text-muted italic px-1">
                                View only
                              </span>
                            </Tooltip>
                          ) : isEd ? (
                            <>
                              <Tooltip title="Save">
                                <IconButton size="small" onClick={saveEditTerm}>
                                  <Save sx={{ fontSize: 13, color: "green" }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton size="small" onClick={cancelEditTerm}>
                                  <Cancel sx={{ fontSize: 13, color: "red" }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              {/* Show edit when at least one component is not locked (paid/partially paid) */}
                              {(!term.isCommissionInvoiceLocked || !term.isBonusInvoiceLocked) && (
                                <Tooltip
                                  title={
                                    term.isCommissionInvoiceLocked
                                      ? "Commission paid/partial — only bonus fields editable"
                                      : term.isBonusInvoiceLocked
                                      ? "Bonus paid/partial — only commission fields editable"
                                      : "Edit term"
                                  }
                                >
                                  <IconButton size="small" onClick={() => startEditTerm(term)}>
                                    <Edit sx={{ fontSize: 13 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {(() => {
                                // Direct claim is offered only for un-invoiced terms that still have
                                // something outstanding and at least one non-zero component. A term
                                // already fully settled (invoice-paid or manually claimed, including
                                // via not-applicable components) has nothing left to direct-claim.
                                const cAmt = Number(term.commission) || 0;
                                const bAmt = Number(term.bonus) || 0;
                                const eligibleDirectClaim =
                                  !term.hasInvoice &&
                                  !isTermFullySettled(term) &&
                                  (cAmt > 0 || bAmt > 0);
                                if (!eligibleDirectClaim) return null;
                                return (
                                  <Tooltip title="Direct claim — create invoice & mark paid (today)">
                                    <IconButton
                                      size="small"
                                      color="warning"
                                      onClick={() => onDirectClaim(admission, term)}
                                    >
                                      <Bolt sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                );
                              })()}
                              {(() => {
                                // Unified with the Claimed column: a component counts as settled once
                                // it's not applicable, claimed (manually or via invoice), or invoice-
                                // paid — so a purely-manual claim (no invoice at all) still resolves to
                                // "✓ Claimed" instead of getting stuck on "No invoice" forever.
                                const allSettled = isTermFullySettled(term);
                                const somePaid =
                                  term.isCommissionInvoicePaid ||
                                  term.isBonusInvoicePaid ||
                                  term.isCommissionClaimed ||
                                  term.isBonusClaimed;
                                const partiallySettled = somePaid && !allSettled;
                                const hasAnyInvoice =
                                  term.hasCommissionInvoice ||
                                  term.hasBonusInvoice ||
                                  term.hasInvoice;

                                if (allSettled)
                                  return (
                                    <Tooltip
                                      title={
                                        hasAnyInvoice
                                          ? "All invoiced components paid — mark invoice as unpaid to unclaim"
                                          : "Claimed manually — nothing invoiced"
                                      }
                                    >
                                      <span className="text-xs text-green-600 font-medium px-1">
                                        ✓ Claimed
                                      </span>
                                    </Tooltip>
                                  );
                                if (partiallySettled || isPartialInvoice(term))
                                  return (
                                    <Tooltip title="Some components settled — the rest still needs an invoice (or claim) to finish">
                                      <span className="text-xs text-amber-600 font-medium px-1">
                                        Partially claimed
                                      </span>
                                    </Tooltip>
                                  );
                                if (hasAnyInvoice)
                                  return (
                                    <Tooltip title="Mark invoice as paid to claim">
                                      <span className="text-xs text-app-text-muted italic px-1">
                                        Via invoice
                                      </span>
                                    </Tooltip>
                                  );
                                return (
                                  <Tooltip title="Create invoice first to enable claiming">
                                    <span className="text-xs text-gray-300 italic px-1">
                                      No invoice
                                    </span>
                                  </Tooltip>
                                );
                              })()}
                              {!term.hasInvoice && !term.isCommissionClaimed && (
                                <Tooltip title="Remove from provider">
                                  <IconButton size="small" onClick={() => onRemoveTerm(term.id)}>
                                    <DeleteOutline sx={{ fontSize: 13, color: "#dc2626" }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Collapse>
    </div>
  );
}

export default AddProvider;
